import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import Avatar from '../components/Avatar.jsx'
import ErrorState from '../components/ErrorState.jsx'
import FormAlert from '../components/FormAlert.jsx'
import { ApiError, api } from '../lib/api.js'

/**
 * `/notifications` — the bell (NOTIF-001, PLAN §7).
 *
 * Each row renders from the notification's own payload, which was written by
 * whatever caused it. That is why this page is one indexed read rather than a
 * fan of joins, and why the `notifications` module knows nothing about what a
 * review is.
 *
 * Mentions are the documented cut from PLAN §25 NOTIF-001 and are not built.
 */
const SENTENCES = {
  follow: 'started following you',
  follow_request: 'asked to follow you',
  follow_accepted: 'accepted your follow request',
  comment: 'commented on your review',
  reaction: 'hearted your review',
}

/**
 * Types with nobody behind them (1B-4b Phase 2b).
 *
 * Every notification until now was one person doing something to another, so
 * the row was built around an actor: an avatar, a name in bold, and a sentence
 * completing it. `tag_adopted` has no actor — `actor_user_id` is null — and
 * rendering it through the same path would draw an avatar for "Someone", which
 * is worse than saying nothing about who.
 */
const SYSTEM = {
  tag_adopted: (payload) =>
    `“${payload.label}” is now a tag, because you suggested it. Add it to a rating when it fits.`,
  // 1B-4c. The word was already there under another spelling, so the sentence
  // above would be false — it did not become a tag, it turned out to be one.
  // Same closing invitation, because Q6's no-auto-attach rule is unchanged:
  // nothing was added to anybody's rating either way.
  tag_aliased: (payload) =>
    `“${payload.label}” is already a tag — your suggestion pointed to it. Add it to a rating when it fits.`,
}

/**
 * W9a to W9c (Q34, Josh 2026-09-24): what was commented on or hearted, from
 * the payload that always said. A row without the key is a review's, which is
 * what every comment and heart was before lists could have them.
 */
function sentence(row) {
  const { type, payload } = row
  if (type === 'comment' && payload.parent_type === 'list') return 'commented on your list'
  if (type === 'reaction' && payload.target_type === 'list') return 'hearted your list'
  if (type === 'reaction' && payload.target_type === 'comment') return 'hearted your comment'
  return SENTENCES[type] || 'did something'
}

function destination(row) {
  const { type, payload } = row
  if (type === 'comment' || type === 'reaction') {
    if (payload.review_public_id) return `/reviews/${payload.review_public_id}`
    if (payload.list_public_id) return `/lists/${payload.list_public_id}`
  }
  return payload.actor ? `/profile/${payload.actor}` : '/'
}

function Row({ row }) {
  const system = SYSTEM[row.type]
  const who = row.payload.actor_display_name || row.payload.actor || 'Someone'
  // A system row is not a link. There is no page for a tag, and a link to `/`
  // would be a promise the row cannot keep.
  const Body = system ? 'div' : Link
  const bodyProps = system ? {} : { to: destination(row) }
  return (
    <li className={`notification ${row.read ? '' : 'unread'}`}>
      <Body {...bodyProps} className="notification-link">
        {system ? null : <Avatar name={who} size={36} avatarUrl={row.actor_avatar_url} />}
        <span className="row-main">
          <span className="notification-text">
            {system ? (
              system(row.payload)
            ) : (
              <>
                <strong>{who}</strong> {sentence(row)}
                {row.payload.title ? ` — ${row.payload.title}` : ''}
              </>
            )}
          </span>
          {row.payload.excerpt ? (
            <span className="footnote row-sub wrap">{row.payload.excerpt}</span>
          ) : null}
          <span className="footnote">{new Date(row.created_at).toLocaleString()}</span>
        </span>
      </Body>
      {row.read ? null : <span className="unread-dot" aria-label="Unread" />}
    </li>
  )
}

export default function Notifications() {
  const [state, setState] = useState({ loading: true, items: [], unread: 0 })
  const [error, setError] = useState('')
  const [markError, setMarkError] = useState('')

  const load = useCallback(async (signal) => {
    try {
      const body = await api.notifications({ signal })
      setState({ loading: false, items: body.items, unread: body.unread })
    } catch (err) {
      if (err.name === 'AbortError') return
      setError(err instanceof ApiError ? err.message : 'Notifications did not load.')
      setState((current) => ({ ...current, loading: false }))
    }
  }, [])

  useEffect(() => {
    document.title = 'Notifications — Sonologue'
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  async function markAll() {
    setMarkError('')
    try {
      await api.markNotificationsRead()
      setState((current) => ({
        ...current,
        unread: 0,
        items: current.items.map((row) => ({ ...row, read: true })),
      }))
    } catch (err) {
      // Nothing moves on failure: the rows, the dots and the button stay as
      // they were, because the server still holds every one of them unread.
      setMarkError(
        err instanceof ApiError ? err.message : 'Could not mark everything read just now.',
      )
    }
  }

  return (
    <>
      <div className="section-header">
        <h1 className="title-2">Notifications</h1>
        {state.unread > 0 ? (
          <button type="button" className="btn btn-quiet btn-sm" onClick={markAll}>
            Mark all read
          </button>
        ) : null}
      </div>

      <FormAlert tone="caution">{markError}</FormAlert>

      <div className="card">
        {error ? (
          <ErrorState
            title="Notifications did not load"
            message={error}
            onRetry={() => load()}
          />
        ) : state.loading ? (
          <div className="card-pad">
            <p className="footnote mb-0">Loading…</p>
          </div>
        ) : state.items.length === 0 ? (
          <div className="empty-state">
            <h2>Nothing yet</h2>
            <p>
              Follows, comments and hearts land here. What people you follow are listening to is
              in <Link to="/">your feed</Link> instead.
            </p>
          </div>
        ) : (
          <ul className="notification-list">
            {state.items.map((row) => (
              <Row key={row.id} row={row} />
            ))}
          </ul>
        )}
      </div>
    </>
  )
}
